import { AssetsManagerService } from '@barthauer/abc/core/assets-manager';
import { formatMessage, loadMessages } from 'devextreme/localization';
import { ResourceManagerService } from './services/resource-manager.service';
import { inject } from '@angular/core';

export const RESOURCES: Map<string, Map<string, object>> = new Map<string, Map<string, object>>();
export let LANGUAGE: string;
export class ResourceManager {
  static assetsManagerService: AssetsManagerService;
  public static readonly DEV_EXTREME = 'DevExtreme';
  constructor(private resourceSet?: string) {
    // ResourceManager.assetsManagerService = new AssetsManagerService(this.httpClient);
    ResourceManager.assetsManagerService = inject(AssetsManagerService);
    if (resourceSet) {
      ResourceManager.loadResource(
        `assets/resources/${resourceSet}.${ResourceManager.getLanguage()}.json`,
        resourceSet,
      );
    }
    // Load devExtreme localisation if needed
    ResourceManager.loadDevExtremeResources();
  }

  /**
   * @static
   * @param {string} resourceSet Ex: 'abc-messages-messageBox'
   * @description The german json file should be named this way: abc-messages-messageBox.de.json
   *
   * @returns function formatMessage(key: string, value?: string | string[])
   * @example
   * const res = ResourceManager.getResources('abc-messages-messageBox');
   * const yesInGerman = res('YES'); // returns Ja
   * @memberof ResourceManager
   */
  public static getResources(resourceSet: string): (key: string, ...value: string[]) => string {
    if (!RESOURCES) {
      new ResourceManager(resourceSet);
    } else {
      ResourceManager.loadResource(
        `assets/resources/${resourceSet}.${ResourceManager.getLanguage()}.json`,
        resourceSet,
      );
    }
    return ResourceManager.formatMessage;
  }

  public static getResourcesPromise(resourceSet: string): Promise<(key: string, ...value: string[]) => string> {
    if (!RESOURCES) {
      new ResourceManager();
    }

    return new Promise((resolve) => {
      ResourceManager.loadResource(
        `assets/resources/${resourceSet}.${ResourceManager.getLanguage()}.json`,
        resourceSet,
      ).then((data) => {
        resolve(ResourceManager.formatMessage);
      });
    });
  }

  private static formatMessage(key: string, ...value: Array<string>) {
    return formatMessage(key, ...value);
  }

  public static getFixedCulture(): string | undefined {
    return ResourceManagerService.getCurrentUserLanguage() ?? ResourceManagerService.getFixedCulture();
  }

  public static setFixedCulture(value: string | undefined): void {
    return ResourceManagerService.setFixedCulture(value);
  }

  public static getLanguage(): string {
    // Removed fallback to LANGUAGE to ensure updated value is fetched; default value ('de') caused mismatch before API loaded
    LANGUAGE = ResourceManager.getLanguageCode(ResourceManagerService.getCurrentLanguage());
    ResourceManagerService.tryApplyLanguage(LANGUAGE);
    return LANGUAGE;
  }

  public static changeLanguage(language: string) {
    LANGUAGE = language;
    ResourceManager.getLanguage();
    ResourceManager.loadDevExtremeResources();
    return RESOURCES?.forEach((value, key) => {
      if (key !== ResourceManager.DEV_EXTREME) {
        ResourceManager.loadResource(`assets/resources/${key}.${ResourceManager.getLanguage()}.json`, key);
      }
    });
  }

  private static loadResource(url: string, resourceSet: string, fallback?: boolean): Promise<void> {
    // if (!ResourceManager.assetsManagerService) {
    //   // if (!ResourceManager.injector) {
    //   // Fallback: construct manually before Angular is ready
    //   // const http = new HttpClient(new HttpXhrBackend({ build: () => new XMLHttpRequest() }));
    //   const assetsManager = inject(AssetsManagerService);
    //   ResourceManager.assetsManagerService = assetsManager;
    //   // } else {
    //   // ResourceManager.assetsManagerService = ResourceManager.injector.get(AssetsManagerService);
    //   // }
    // }
    return new Promise((resolve) => {
      if (resourceSet) {
        if (RESOURCES?.has(resourceSet) && RESOURCES.get(resourceSet)?.has(ResourceManager.getLanguage())) {
          resolve(loadMessages(RESOURCES.get(resourceSet)?.get(ResourceManager.getLanguage())));
        } else {
          ResourceManager.assetsManagerService?.getFile(url).subscribe({
            next: (file) => {
              const key = Object.keys(file)[0];
              if (RESOURCES.has(resourceSet)) {
                RESOURCES.get(resourceSet)?.set(key, file[key]);
              } else {
                const map: Map<string, object> = new Map();
                map.set(key, file[key]);
                RESOURCES.set(resourceSet, map);
              }
              resolve(loadMessages(file));
            },
            error: (err) => {
              if (!fallback) {
                const fallbackUrl = `assets/resources/${resourceSet}.de.json`;
                ResourceManagerService.tryApplyLanguage('de');
                ResourceManager.loadResource(fallbackUrl, resourceSet, true);
              } else {
                // In case even the 'de' resource is not found
                console.log('Resource url ' + url + ' not found');
                console.log(err?.message);
              }
            },
          });
        }
      }
    });
  }

  public static loadDevExtremeResources(): void {
    if (
      !RESOURCES.has(ResourceManager.DEV_EXTREME) ||
      (RESOURCES.has(ResourceManager.DEV_EXTREME) &&
        !RESOURCES.get(ResourceManager.DEV_EXTREME)?.has(ResourceManager.getLanguage()))
    ) {
      if (ResourceManager.getLanguage() === 'ar') {
        ResourceManager.loadResource(
          `assets/resources/${ResourceManager.DEV_EXTREME}.${ResourceManager.getLanguage()}.json`,
          ResourceManager.DEV_EXTREME,
        );
      } else {
        import(`devextreme/localization/messages/${LANGUAGE}.json`).then((messages) => {
          if (RESOURCES.has(ResourceManager.DEV_EXTREME)) {
            RESOURCES.get(ResourceManager.DEV_EXTREME)?.set(LANGUAGE, messages[LANGUAGE]);
          } else {
            const map: Map<string, object> = new Map();
            map.set(LANGUAGE, messages[LANGUAGE]);
            RESOURCES.set(ResourceManager.DEV_EXTREME, map);
          }
          loadMessages(messages[LANGUAGE]);
        });
      }
    } else {
      const obj = RESOURCES.get(ResourceManager.DEV_EXTREME)?.get(ResourceManager.getLanguage());
      loadMessages(obj);
    }
  }

  private static getLanguageCode(ln: string): string {
    switch (ln) {
      case 'de-DE':
      case 'de':
        return 'de';
      case 'en-US':
      case 'en':
        return 'en';
      case 'fr-FR':
      case 'fr':
        return 'fr';
      case 'ar':
        return 'ar';
      case 'pt':
        return 'pt';
      case 'sp':
        return 'sp';
      default:
        return 'de';
    }
  }
}
